import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CalendarDays, MapPin, HeartHandshake, Megaphone, Trophy } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { LOGO_URL, MISSION, VISION, LEAGUE } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Hero() {
  return (
    <section className="relative min-h-[88vh] flex items-center overflow-hidden bg-[#0A0A0A]" data-testid="home-hero">
      <div className="absolute inset-y-0 right-0 hidden lg:flex items-center pointer-events-none" aria-hidden="true">
        <img src={LOGO_URL} alt="" className="w-[600px] opacity-15 translate-x-24" />
      </div>
      <div className="absolute left-0 inset-y-0 w-1.5 bg-[#FEE123]" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
        <div className="max-w-3xl">
          <Reveal>
            <img src={LOGO_URL} alt="Louisiana Ducks team logo" className="h-28 w-28 sm:h-36 sm:w-36 object-contain drop-shadow-[0_0_30px_rgba(254,225,35,0.35)]" data-testid="hero-logo" />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-sm font-bold uppercase tracking-[0.3em] text-[#FEE123]" data-testid="hero-eyebrow">
              St. Charles Parish &bull; New Orleans Metro Area &bull; Kicking Off 2027
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <h1 className="font-display text-6xl sm:text-7xl md:text-8xl uppercase leading-[0.9] mt-4 text-white" data-testid="hero-title">
              The Only National <span className="text-[#FEE123]">Youth Team</span> In South Louisiana
            </h1>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-7 text-base sm:text-lg text-gray-200 leading-relaxed max-w-2xl" data-testid="hero-subtitle">
              {LEAGUE}
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/donate"
                data-testid="hero-donate-button"
                className="inline-flex items-center justify-center gap-2 bg-[#FEE123] text-black text-lg font-extrabold uppercase tracking-widest px-8 py-4 rounded-sm transition-transform duration-200 hover:scale-105 hover:bg-[#FFEA61]"
              >
                Donate <ArrowRight size={20} />
              </Link>
              <Link
                to="/about"
                data-testid="hero-about-button"
                className="inline-flex items-center justify-center gap-2 bg-[#0F4D22] text-white text-lg font-bold uppercase tracking-widest px-8 py-4 rounded-sm border border-[#FEE123]/30 transition-all duration-200 hover:scale-105 hover:bg-[#146B2F]"
              >
                Our Mission
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function MissionVision() {
  return (
    <section className="py-14 sm:py-20 bg-[#0A0A0A]" data-testid="home-mission-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full bg-[#111111] border border-[#333333] border-l-4 border-l-[#FEE123] p-8 sm:p-10" data-testid="mission-card">
              <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#FEE123]">Our Mission</p>
              <p className="mt-5 text-lg sm:text-xl text-gray-200 leading-relaxed font-medium">{MISSION}</p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="h-full bg-[#111111] border border-[#333333] border-l-4 border-l-[#0F4D22] p-8 sm:p-10" data-testid="vision-card">
              <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#FEE123]">Our Vision</p>
              <p className="mt-5 text-lg sm:text-xl text-gray-200 leading-relaxed font-medium">{VISION}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function NonProfitBanner() {
  return (
    <section className="bg-[#FEE123]" data-testid="home-nonprofit-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center">
        <HeartHandshake size={48} className="text-black mx-auto" />
        <h2 className="font-display text-5xl sm:text-6xl md:text-7xl uppercase leading-[0.95] text-black mt-6 max-w-5xl mx-auto">
          Louisiana Ducks Is A Non-Profit Youth Sports Organization
        </h2>
        <p className="mt-6 text-base sm:text-lg font-extrabold uppercase tracking-[0.25em] text-black/70">
          Every Donation Fuels Our Programs
        </p>
      </div>
    </section>
  );
}

function TryoutsBanner() {
  return (
    <section className="bg-[#111111] border-b border-[#FEE123]/30" data-testid="home-tryouts-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-center gap-3">
        <Megaphone size={20} className="text-[#FEE123] shrink-0" />
        <p className="text-white text-xs sm:text-sm font-bold uppercase tracking-widest text-center">
          2027 Tryouts begin this February — <span className="text-[#FEE123]">more info coming soon</span>
        </p>
      </div>
    </section>
  );
}

function NationalStage() {
  return (
    <section className="py-14 sm:py-20 bg-[#06220F]" data-testid="home-national-stage-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center gap-10">
          <Reveal>
            <div className="shrink-0 w-24 h-24 flex items-center justify-center bg-[#FEE123] rounded-sm">
              <Trophy size={48} className="text-black" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#FEE123]" data-testid="national-stage-eyebrow">
              American Youth Football
            </p>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl uppercase leading-none mt-3 text-white">
              The Biggest Stage In Youth Football
            </h2>
            <p className="mt-5 text-base sm:text-lg text-gray-200 leading-relaxed max-w-3xl" data-testid="national-stage-text">
              {LEAGUE}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ScheduleTeaser() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/schedule`)
      .then((res) => setEvents(res.data.slice(0, 3)))
      .catch(() => setEvents([]));
  }, []);

  if (events.length === 0) return null;

  return (
    <section className="py-14 sm:py-20 bg-[#0A0A0A]" data-testid="home-schedule-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <SectionHeading eyebrow="Mark The Calendar" title="Upcoming" testid="home-schedule-heading" />
          <Link
            to="/schedule"
            data-testid="home-schedule-viewall"
            className="gold-underline text-sm font-bold uppercase tracking-widest text-[#FEE123] whitespace-nowrap"
          >
            Full Schedule
          </Link>
        </div>
        <div className="mt-8 space-y-4">
          {events.map((event, i) => (
            <Reveal key={event.id} delay={i * 0.1}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-[#111111] border border-[#333333] p-6" data-testid={`home-event-${i}`}>
                <div className="flex items-center gap-3 sm:w-64 shrink-0">
                  <CalendarDays size={20} className="text-[#FEE123] shrink-0" />
                  <span className="text-sm font-bold uppercase tracking-widest text-[#FEE123]">{event.date_label}</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold uppercase tracking-wide text-white">{event.title}</h3>
                  <p className="mt-1 text-sm text-gray-400 flex items-center gap-2">
                    <MapPin size={14} /> {event.location} &bull; {event.time_label}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function DonateCta() {
  return (
    <section className="relative py-14 sm:py-20 overflow-hidden bg-[#0F4D22]" data-testid="home-donate-cta">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#FEE123]">Fuel The Flock</p>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl uppercase leading-none mt-4 text-white max-w-3xl">
            Every Dollar Builds A Duck
          </h2>
          <p className="mt-6 text-base sm:text-lg text-gray-200 leading-relaxed max-w-2xl">
            Your donation funds sponsorship opportunities for qualifying athletes, tutoring, mentorship, and college exposure
            experiences — right here in the New Orleans metro area.
          </p>
          <Link
            to="/donate"
            data-testid="home-donate-cta-button"
            className="mt-10 inline-flex items-center gap-2 bg-[#FEE123] text-black text-lg font-extrabold uppercase tracking-widest px-8 py-4 rounded-sm transition-transform duration-200 hover:scale-105 hover:bg-[#FFEA61]"
          >
            Donate Now <ArrowRight size={20} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function InterestMeetingCta() {
  return (
    <section className="bg-[#FEE123]" data-testid="home-interest-meeting-cta">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 flex flex-col lg:flex-row lg:items-center gap-10">
        <div className="flex-1">
          <p className="text-sm font-extrabold uppercase tracking-[0.3em] text-black/70" data-testid="interest-meeting-eyebrow">
            Parents &amp; Athletes
          </p>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl uppercase leading-[0.95] text-black mt-3">
            Come To Our Interest Meeting
          </h2>
          <p className="mt-5 text-base sm:text-lg font-bold text-black/80 max-w-2xl">
            Meet the directors, hear the 2027 vision, and claim your athlete's spot before February tryouts.
          </p>
        </div>
        <Link
          to="/join"
          data-testid="interest-meeting-button"
          className="shrink-0 inline-flex items-center gap-2 bg-black text-[#FEE123] text-lg font-extrabold uppercase tracking-widest px-10 py-5 rounded-sm transition-transform duration-200 hover:scale-105"
        >
          Join The Flock <ArrowRight size={20} />
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <NonProfitBanner />
      <TryoutsBanner />
      <MissionVision />
      <NationalStage />
      <ScheduleTeaser />
      <DonateCta />
      <InterestMeetingCta />
    </>
  );
}
